import type { LucideIcon } from 'lucide-react'
import type { FC, ReactNode } from 'react'

import {
    Clock3,
    ExternalLink,
    MapPin,
    Navigation,
    Phone,
    TrainFront,
    Users
} from 'lucide-react'

import { mapEmbedUrl } from '@/lib/business'
import { OpenStatus } from '@/components/generals/open-status'
import { SectionHeading } from '@/components/generals/section-heading'

import {
    CallButton,
    DirectionsButton,
    WhatsAppButton,
    buttonClass
} from '@/components/generals/cta-buttons'

import { address, contact, hours } from '@/constants/site'

interface InfoCardProps {
    icon: LucideIcon
    title: string
    children: ReactNode
}

const InfoCard: FC<InfoCardProps> = ({ icon: Icon, title, children }): ReactNode => {
    return (
        <div className='flex gap-4 rounded-2xl border border-border bg-white p-5 shadow-sm'>
            <span className='gold-gradient flex size-11 shrink-0 items-center justify-center rounded-xl text-plum-deep'>
                <Icon className='size-5' aria-hidden='true' />
            </span>

            <div className='min-w-0 flex-1'>
                <h3 className='font-heading text-sm font-semibold tracking-wide text-plum'>
                    {title}
                </h3>

                <div className='mt-1.5 text-sm leading-relaxed text-foreground/85'>
                    {children}
                </div>
            </div>
        </div>
    )
}

export const VisitSection: FC = (): ReactNode => {
    return (
        <section
            id='visit'
            aria-labelledby='visit-title'
            className='blush-glow bg-white py-20 sm:py-24 lg:py-28'
        >
            <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                <SectionHeading
                    id='visit-title'
                    eyebrow='Visit us'
                    title='Find us in New Gupta Colony'
                    description='We are in Kalyan Vihar, North Delhi, a short ride from Model Town Metro. Tap "Get directions" for the route from wherever you are right now.'
                />

                <div className='mt-12 grid gap-6 lg:grid-cols-[1.25fr_1fr] lg:gap-8'>
                    <div className='flex flex-col overflow-hidden rounded-3xl border border-gold/30 bg-white shadow-xl shadow-plum/10'>
                        <div className='relative aspect-[4/3] w-full bg-muted sm:aspect-[16/10] lg:aspect-auto lg:min-h-[24rem] lg:flex-1'>
                            <iframe
                                src={mapEmbedUrl()}
                                title='Map showing Anushka Beauty Parlour in New Gupta Colony, Kalyan Vihar, Delhi'
                                loading='lazy'
                                referrerPolicy='no-referrer-when-downgrade'
                                allowFullScreen
                                className='absolute inset-0 size-full border-0'
                            />
                        </div>

                        <div className='flex flex-col gap-4 border-t border-border p-4 sm:p-5 xl:flex-row xl:items-center xl:justify-between'>
                            <p className='flex items-start gap-2 text-sm text-muted-foreground'>
                                <Navigation className='mt-0.5 size-4 shrink-0 text-gold-dark' aria-hidden='true' />
                                Opens Google Maps with directions from your current location.
                            </p>

                            <div className='flex flex-col gap-2 sm:flex-row'>
                                <DirectionsButton variant='plum' label='Get directions' className='shrink-0' />

                                <a
                                    href={contact.mapsUrl}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className={buttonClass('outline-dark', 'md', 'shrink-0')}
                                >
                                    <ExternalLink className='size-4' aria-hidden='true' />
                                    Open in Google Maps
                                </a>
                            </div>
                        </div>
                    </div>

                    <div className='grid content-start gap-4 sm:grid-cols-2 lg:grid-cols-1'>
                        <InfoCard icon={MapPin} title='Address'>
                            <address className='not-italic'>
                                {address.lineOne}
                                <br />
                                {address.lineTwo}
                            </address>

                            <p className='mt-1 text-xs text-muted-foreground'>
                                {address.landmark}
                            </p>
                        </InfoCard>

                        <InfoCard icon={TrainFront} title='Nearest metro'>
                            <p>{address.nearestMetro}</p>

                            <p className='mt-1 text-xs text-muted-foreground'>
                                Close to GTB Nagar, Vijay Nagar and Delhi University North Campus
                            </p>
                        </InfoCard>

                        <InfoCard icon={Clock3} title='Opening hours'>
                            <p>{hours.days}</p>

                            <p className='font-semibold text-plum'>{hours.label}</p>

                            <OpenStatus className='mt-3' />
                        </InfoCard>

                        <InfoCard icon={Phone} title='Call or WhatsApp'>
                            <a href={contact.phoneHref} className='text-base font-semibold text-plum transition-colors hover:text-gold-dark'>
                                {contact.phoneDisplay}
                            </a>

                            <div className='mt-3 flex flex-wrap gap-2'>
                                <CallButton label='Call now' variant='plum' size='sm' />
                                <WhatsAppButton label='WhatsApp' variant='whatsapp' size='sm' />
                            </div>
                        </InfoCard>

                        <InfoCard icon={Users} title='Women-only parlour'>
                            <p>A comfortable, private space where you can relax and be pampered.</p>
                        </InfoCard>
                    </div>
                </div>
            </div>
        </section>
    )
}
